/* RadarWidget — Widget Noam Radar pour Executive 360°.
 *
 * Affiche : nb signaux non lus + 3 derniers signaux cliquables + bouton
 * "Voir tout" qui ouvre le drawer Radar. Ratrapage du compteur en temps
 * réel via polling 30s. Inactif si TAVILY_API_KEY absente côté backend.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Radio, ExternalLink, Loader2, AlertTriangle } from "lucide-react";

export default function RadarWidget({ compact = false }) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/workspace/radar/summary"),
        api.get("/workspace/radar/feed", { params: { limit: 4 } }),
      ]);
      setSummary(s.data);
      setItems(f.data?.items || []);
    } catch {/* silent */}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="noam-card p-4 flex items-center gap-2 text-[11px] text-zinc-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Chargement radar…
      </div>
    );
  }

  if (summary && !summary.active) {
    return (
      <Link
        to="/app/radar"
        className="noam-card p-4 space-y-2 block hover:border-[#EF9F27]/40 transition-colors group"
        data-testid="radar-widget-inactive"
      >
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-[#EF9F27]" />
          <div className="noam-overline">Noam Radar</div>
          <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[#EF9F27]">
            Veille inactive
          </span>
        </div>
        <div className="text-[11px] text-zinc-400 flex items-start gap-1.5 leading-relaxed">
          <AlertTriangle className="w-3 h-3 mt-0.5 text-[#EF9F27] shrink-0" />
          <span>
            La veille externe (Tavily) n&apos;est pas encore configurée. Clique ici pour ouvrir Noam Radar et démarrer un scan manuel — ou demande à un administrateur d&apos;ajouter la clé <code className="px-1 bg-white/10 rounded">TAVILY_API_KEY</code>.
          </span>
        </div>
        <div className="text-[10px] font-medium text-[#EF9F27] flex items-center gap-1 group-hover:gap-1.5 transition-all">
          Ouvrir Radar <ExternalLink className="w-2.5 h-2.5" />
        </div>
      </Link>
    );
  }

  return (
    <div className="noam-card p-4 space-y-3" data-testid="radar-widget">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Radio className="w-3.5 h-3.5 text-[#1D9E75]" />
            {summary?.unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-[#EF9F27] text-[9px] font-bold flex items-center justify-center text-black">
                {summary.unread > 9 ? "9+" : summary.unread}
              </span>
            )}
          </div>
          <div>
            <div className="noam-overline">Noam Radar</div>
            <div className="text-[11px] text-zinc-400">
              {summary?.unread || 0} signaux non lus · {summary?.total || 0} total
            </div>
          </div>
        </div>
        <Link
          to="/app/radar"
          className="btn-ghost text-[10px]"
          data-testid="radar-widget-open"
        >
          Voir tout
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="text-[11px] text-zinc-500 italic">
          Aucun signal capté pour le moment. Le prochain scan aura lieu sous 24h.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, compact ? 2 : 4).map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-2 p-2 rounded-md border border-white/5 hover:border-[#378ADD]/30 transition-colors"
              data-testid={`radar-widget-item-${s.id}`}
            >
              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${s.read ? "bg-zinc-700" : "bg-[#EF9F27]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-zinc-200 truncate" title={s.title}>{s.title}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-zinc-500 truncate max-w-[140px]">{s.entity_name}</span>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#378ADD] hover:underline"
                  >
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
